import React, { ReactNode } from "react";
import { DateBlockIcon } from "shared/icons/BlockIcons/DateBlockIcon";
import { LongTextBlockIcon } from "shared/icons/BlockIcons/LongTextBlockIcon";
import { MainSurveyBlockIcon } from "shared/icons/BlockIcons/MainSurveyBlockIcon";
import { MultiSelectBlockIcon } from "shared/icons/BlockIcons/MultiSelectBlockIcon";
import { NumberBlockIcon } from "shared/icons/BlockIcons/NumberBlockIcon";
import { RichTextBlockIcon } from "shared/icons/BlockIcons/RichTextBlockIcon";
import { ShortTextBlockIcon } from "shared/icons/BlockIcons/ShortTextBlockIcon";
import { SingleSelectBlockIcon } from "shared/icons/BlockIcons/SingleSelectBlockIcon";
import { SliderBlockIcon } from "shared/icons/BlockIcons/SliderBlockIcon";
import { SurveyBlockIcon } from "shared/icons/BlockIcons/SurveyBlockIcon";
import { XMarkIcon } from "shared/icons/XMarkIcon";
import { BlockFieldTypes } from "./blockForm";
import { FormulaBlockIcon } from "shared/icons/BlockIcons/FormulaBlockIcon";
import { ChartBlockIcon } from "shared/icons/BlockIcons/ChartBlockIcon";
import { TableBlockIcon } from "shared/icons/BlockIcons/TableBlockIcon";

export enum BlockTypes {
  MainSurvey = "mainSurvey",
  Survey = "survey",
  Chart = "survey/chart",
  SingleSelect = "questionradiobutton",
  MultiSelect = "questioncheckbox",
  LongText = "questionlongtext",
  ShortText = "questionshorttext",
  Number = "questionnumber",
  Date = "questiondate",
  Slider = "questionslider",
  RichText = "formattingrichtext",
  Error = "error",
  Formula = "formula",
  Table = "survey/table",
}

export enum BlockSubTypes {
  Chart = "chart",
  Table = "table",
}

export enum BlockLayoutTypes {
  Structure = "Structure",
  Questions = "Questions",
  Text = "Text",
  Advanced = "Advanced",
}

export const BlockOptionLayouts: BlockOptionLayout[] = [
  {
    type: BlockLayoutTypes.Structure,
    color: "var(--c-structure-block)",
    options: [
      {
        blockType: BlockTypes.Survey,
        name: "Survey",
        icon: <SurveyBlockIcon />,
        isHasNested: true,
        fields: [
          { key: BlockFieldTypes.Layout, title: "Layout" },
          { key: BlockFieldTypes.Preview, title: "Preview" },
        ],
      },
      {
        blockType: BlockTypes.Chart,
        name: "Chart",
        icon: <ChartBlockIcon />,
        isHasNested: true,
        fields: [
          { key: BlockFieldTypes.Layout, title: "Layout" },
          { key: BlockFieldTypes.MaxRows, title: "Max rows", isAdvanced: true },
          { key: BlockFieldTypes.MaxColumns, title: "Max columns", isAdvanced: true },
          { key: BlockFieldTypes.FormattingInstructions, title: "Formatting instructions", isAdvanced: true}
        ],
      },
      {
        blockType: BlockTypes.Table,
        name: "Table",
        icon: <TableBlockIcon />,
        isHasNested: true,
        fields: [
          { key: BlockFieldTypes.Layout, title: "Layout" },
          { key: BlockFieldTypes.MaxRows, title: "Max rows", isAdvanced: true },
          { key: BlockFieldTypes.MaxColumns, title: "Max columns", isAdvanced: true },
          { key: BlockFieldTypes.FormattingInstructions, title: "Formatting instructions", isAdvanced: true}
        ],
      },
    ],
  },
  {
    type: BlockLayoutTypes.Questions,
    color: "var(--c-question-block)",
    options: [
      {
        blockType: BlockTypes.SingleSelect,
        name: "Single select",
        icon: <SingleSelectBlockIcon />,
        fields: [
          { key: BlockFieldTypes.Options, title: "Options" },
          { key: BlockFieldTypes.QuestionLayout, title: "Layout" },
          { key: BlockFieldTypes.Legend, title: "Legend", isOptional: true },
        ],
      },
      {
        blockType: BlockTypes.MultiSelect,
        name: "Multi select",
        icon: <MultiSelectBlockIcon />,
        fields: [{ key: BlockFieldTypes.Options, title: "Options" }],
      },
      {
        blockType: BlockTypes.LongText,
        name: "Long text",
        icon: <LongTextBlockIcon />,
        fields: [],
      },
      {
        blockType: BlockTypes.ShortText,
        name: "Short text",
        icon: <ShortTextBlockIcon />,
        fields: [],
      },
      {
        blockType: BlockTypes.Number,
        name: "Number",
        icon: <NumberBlockIcon />,
        fields: [{ key: BlockFieldTypes.AllowDecimal, title: "Allow decimal" }],
      },
      {
        blockType: BlockTypes.Date,
        name: "Date",
        icon: <DateBlockIcon />,
        fields: [],
      },
      {
        blockType: BlockTypes.Slider,
        name: "Slider",
        icon: <SliderBlockIcon />,
        fields: [
          { key: BlockFieldTypes.Range, title: "Range" },
          { key: BlockFieldTypes.Options, title: "Options" },
        ],
      },
    ],
  },
  {
    type: BlockLayoutTypes.Text,
    color: "var(--c-text-block)",
    options: [
      {
        blockType: BlockTypes.RichText,
        name: "Rich text",
        icon: <RichTextBlockIcon />,
        fields: [{ key: BlockFieldTypes.Content, title: "Content" }],
      },
    ],
  },
  {
    type: "Advanced",
    color: "var(--c-advanced-block)",
    options: [
      {
        blockType: BlockTypes.Formula,
        name: "Formula",
        icon: <FormulaBlockIcon />,
        fields: [
          { key: BlockFieldTypes.Tooltip, title: "Tooltip", isOptional: true },
          { key: BlockFieldTypes.Subtitle, title: "Subtitle", isOptional: true },
          {
            key: BlockFieldTypes.Expression,
            title: "Expression",
            subtitle: "Example: {Field_id_1}*{Field_id_2}/{Field_id_3}",
            isOptional: true,
          },
        ],
      },
    ],
  },
];

BlockOptionLayouts.find((ol) => ol.type === "Questions")?.options.forEach((option) => {
  option.fields.unshift(
    { key: BlockFieldTypes.IsRequired, title: "Required" },
    { key: BlockFieldTypes.Tooltip, title: "Tooltip", isOptional: true },
    { key: BlockFieldTypes.Subtitle, title: "Subtitle", isOptional: true }
  );
});

export const MainBlockOptionLayout: BlockOptionLayout = {
  type: "MainSurvey",
  color: "var(--c-main-block)",
  options: [
    {
      blockType: BlockTypes.MainSurvey,
      name: "Main survey",
      icon: <MainSurveyBlockIcon />,
      isHasNested: true,
      fields: [
        { key: BlockFieldTypes.Tags, title: "Tags" },
        { key: BlockFieldTypes.Layout, title: "Layout" },
      ],
    },
  ],
};

export const BlockOptions = BlockOptionLayouts.reduce((acc, curr) => {
  return acc.concat(curr.options.map((option) => ({ ...option, color: curr.color })));
}, [] as BlockOption[]);

export const getBlockOption = (blockType: string): BlockOption => {
  if (blockType === BlockTypes.MainSurvey) {
    return { ...MainBlockOptionLayout.options[0], color: MainBlockOptionLayout.color };
  }

  return BlockOptions.find((option) => option.blockType === blockType) ?? ErrorOption;
};

export const getBlockFullType = (blockType: string, blockSubType?: string): BlockTypes =>
  blockSubType && blockType !== blockSubType
    ? (`${blockType}/${blockSubType}` as BlockTypes)
    : (blockType as BlockTypes);

export const getSplitBlockType = (blockType: BlockTypes): { blockType: BlockTypes; blockSubType?: BlockSubTypes } => {
  const [type, subType] = blockType.split("/");
  return { blockType: type as BlockTypes, blockSubType: subType as BlockSubTypes | undefined };
};

export const FieldIdPrefixes = {
  [BlockTypes.ShortText]: "short_text",
  [BlockTypes.LongText]: "long_text",
  [BlockTypes.Number]: "number",
  [BlockTypes.Date]: "date",
  [BlockTypes.MultiSelect]: "checkbox",
  [BlockTypes.SingleSelect]: "radio_button",
  [BlockTypes.Slider]: "slider",
  [BlockTypes.Formula]: "formula",
} as { [key: string]: string };

const ErrorOption: BlockOption = {
  blockType: BlockTypes.Error,
  name: "Error",
  icon: <XMarkIcon />,
  color: "var(--c-error)",
  fields: [],
};

interface BlockOptionLayout {
  type: string;
  color: string;
  options: Omit<BlockOption, "color">[];
}

export interface BlockFieldOption {
  key: BlockFieldTypes;
  title: string;
  subtitle?: string;
  isOptional?: boolean;
  isAdvanced?: boolean;
}

export interface BlockOption {
  blockType: BlockTypes;
  blockSubType?: BlockSubTypes;
  name: string;
  icon: ReactNode;
  color: string;
  fields: BlockFieldOption[];
  isHasNested?: boolean;
}
